// pages/stations/BarDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  Check,
  CheckCircle2,
  LogOut,
  RefreshCw,
  Timer,
  Truck,
  Users,
  Wine,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { useRealtimeConnection, useRealtimeEvent } from "@/lib/realtime";
import { applyRealtimeEvent, hasLocalSnapshot, loadDashboardBundle } from "@/lib/localDb";
import { mergeDeptOrders, replaceStationOrders, sameOrderIdentity } from "@/lib/mergeState";
import { localWrite } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";
import { PrepExecutionPanel, PrepModeButton } from "@/components/prep/PrepExecutionPanel";
import { fireAndForgetMesh } from "@/lib/lanMesh";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import { useConnectivity } from "@/hooks/useConnectivity";
import { KitchenTimeAgo, LongWaitTicketBadge, StationDepartmentPicker, ticketWaitMinutes, useStationClock, useStationDepartmentScope } from "@/pages/stations/stationShared";
import { ChimeVolumeControl, playBarChime, useLoginChimeOnMount } from "@/app/chimes";
import {
  BACKEND_URL,
  clearSession,
  confirmSession,
  confirmSessionDeadThen,
  getSession,
  http,
  isTransientCloudError,
  shouldKickOutOn401,
  useAutoInstall,
  useOfflineSessionUpgrade,
} from "@/app/core";
import { beginLogoutSuppression, exitFullscreenIfActive, FullscreenToggleButton, ThemeToggleButton, useCoalescedUpdater } from "@/app/fullscreen";
import { useAutoLogoutOnHidden } from "@/app/fragments";



// ---- BarDashboardPage -----------------------------------------------------
// Mirror of the kitchen dashboard, scoped to drinks. Polls /bar/orders every
// 5s and filters each incoming order to only the bar-routed line items.
// Uses an indigo accent (instead of kitchen's primary) so staff can tell at
// a glance which station they're on.
export function BarDashboardPage() {
  const connectivity = useConnectivity(); // Session 30.8 — LAN/internet tri-state
  const stationNow = useStationClock(); // Session 30.21 — long-wait flags
  const [longWaitMinutes, setLongWaitMinutes] = useState(15);
  const navigate = useNavigate();
  useLoginChimeOnMount();
  const [session, setSessionState] = useState(() => getSession("bar"));
  useOfflineSessionUpgrade("bar", session, setSessionState); // Session 46
  useEffect(() => { try { confirmSession("bar"); } catch (_) { /* noop */ } }, []);
  const headers = useMemo(() => (session?.token ? { Authorization: `Bearer ${session.token}` } : null), [session]);
  // Session 63 — which bar department this screen operates.
  const deptScope = useStationDepartmentScope("bar", headers);
  // Session 41 — station prep execution (full-screen, bar sheets only).
  const [prepMode, setPrepMode] = useState(false);
  useAutoInstall(session);
  // Session 30.24 — Bar "Keep until delivered" (independent flag from the
  // kitchen's; same mechanics — see KitchenDashboardPage).
  const KEEP_KEY = "nfcs:bar:keep-until-delivered";
  const [keepUntilDelivered, setKeepUntilDelivered] = useState(() => {
    try { return localStorage.getItem(KEEP_KEY) === "1"; } catch (_) { return false; }
  });
  const keepUntilDeliveredRef = useRef(keepUntilDelivered);
  const applyKeepSetting = useCallback((value) => {
    const v = !!value;
    keepUntilDeliveredRef.current = v;
    setKeepUntilDelivered(v);
    try { localStorage.setItem(KEEP_KEY, v ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [savingKeep, setSavingKeep] = useState(false);
  // Session 55 — TEAM MODE. Several bartenders share one screen: a ticket
  // must be claimed ("I've got this") before it can be marked ready, so two
  // people never pour the same drink. Business-wide flag (server), mirrored
  // locally so the screen paints right on cold start.
  const TEAM_KEY = "nfcs:bar:team-mode";
  const [teamMode, setTeamMode] = useState(() => {
    try { return localStorage.getItem(TEAM_KEY) === "1"; } catch (_) { return false; }
  });
  const teamModeRef = useRef(teamMode);
  const applyTeamMode = useCallback((value) => {
    const v = !!value;
    teamModeRef.current = v;
    setTeamMode(v);
    try { localStorage.setItem(TEAM_KEY, v ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [savingTeam, setSavingTeam] = useState(false);
  const [claimBusy, setClaimBusy] = useState(null); // order id in flight
  const _isBarDeliveredAll = (items) => {
    const live = (items || []).filter((it) => it && !it.voided);
    // Session 73 — a ticket whose every live line was pruned (all voided
    // elsewhere) has nothing left for this station: treat as done.
    return live.every((it) => String(it.status || "").toLowerCase() === "delivered");
  };
  const _isAwaitingDelivery = (items) => {
    const live = (items || []).filter((it) => it && !it.voided);
    return live.length > 0 && live.every((it) => ["ready", "delivered"].includes(String(it.status || "").toLowerCase()));
  };
  const _applyItemStatuses = (order, snapshot, full = false) => {
    const byId = new Map((snapshot || []).map((it) => [String(it.id || ""), it]).filter(([k]) => k !== ""));
    if (byId.size === 0) return order;
    // Session 73 — `full` means the snapshot is the server's WHOLE live
    // item list (`all_items`, voided lines already excluded). A local line
    // with a stable id that is missing from it was voided on another
    // device (event missed) — drop it, otherwise the ticket stays on the
    // screen "pending" forever after everything else was delivered.
    const items = (order.items || []).flatMap((it) => {
      const id = String(it.id || "");
      const s = byId.get(id);
      if (full && id !== "" && !s) return [];
      return [s && s.status ? { ...it, status: s.status } : it];
    });
    return { ...order, items, bar_awaiting_delivery: _isAwaitingDelivery(items) };
  };
  // Local-first paint mirrored from KitchenDashboardPage — seed the
  // ticket list synchronously from the last cached response so the
  // dashboard opens instantly on a cold reopen (no skeleton flash).
  const [orders, setOrdersRaw] = useState(() => {
    try {
      const raw = sessionStorage.getItem("nfcs:bar:last-orders");
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.orders) ? parsed.orders : [];
    } catch (_) { return []; }
  });
  // Session 63 — scoped writer (see KitchenDashboardPage).
  // Session 72 — ONE SOURCE OF TRUTH (see KitchenDashboardPage): local / WS
  // writes are stamped; a quiet screen lets the server list replace state.
  const lastActivityAtRef = useRef(0);
  const QUIET_BEFORE_AUTHORITATIVE_MS = 2500;
  const forceAuthoritativeRef = useRef(false); // Session 73 — set by the safety-net poll
  const setOrders = useCallback((updater) => {
    lastActivityAtRef.current = Date.now();
    setOrdersRaw((prev) => deptScope.scopeOrders(typeof updater === "function" ? updater(prev) : updater));
    // Depends on the memoised scopeOrders fn only — the deptScope object
    // itself is rebuilt per render and would churn every subscriber.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptScope.scopeOrders]);
  const [loading, setLoading] = useState(() => {
    try {
      const raw = sessionStorage.getItem("nfcs:bar:last-orders");
      return !(raw && JSON.parse(raw)?.orders?.length);
    } catch (_) { return true; }
  });
  const [readyingId, setReadyingId] = useState(null);
  // Session 27.101 — Per-order item-selection map (order_id → Set<item_id>).
  // Enables partial-ready — chef ticks the items ready and sends
  // just those. Cleared as soon as the send succeeds.
  const [selectedItems, setSelectedItems] = useState({});
  const _toggleItem = (orderId, itemId) => {
    setSelectedItems((prev) => {
      const set = new Set(prev[orderId] || []);
      if (set.has(itemId)) set.delete(itemId); else set.add(itemId);
      return { ...prev, [orderId]: set };
    });
  };
  const _markSelectedReady = async (orderId) => {
    const keys = Array.from(selectedItems[orderId] || []);
    if (!keys.length) { toast.error("Select at least one item"); return; }
    const indexes = keys.map((k) => parseInt(k, 10)).filter((n) => !Number.isNaN(n));
    const indexSet = new Set(indexes);
    const orderData = orders.find((o) => o.id === orderId);
    // Session 31.2 — count exactly the rows the chef can tick (live, not
    // yet ready) and send stable item ids alongside the positions so the
    // server never flags the wrong line after a void shifted the list.
    const visibleCount = (orderData?.items || []).filter(
      (it) => it && !it.voided && it.status !== "ready" && it.status !== "delivered"
    ).length;
    const selectedIds = indexes.map((i) => orderData?.items?.[i]?.id).filter(Boolean);
    const isFullSend = indexes.length >= visibleCount;

    // Session 27.127 — Optimistic UI first, then await API (mirror of Kitchen).
    if (keepUntilDeliveredRef.current) {
      // Session 30.24 — keep the card; flip sent items to ready.
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const updatedItems = (o.items || []).map((it, idx) =>
          indexSet.has(idx) ? { ...it, status: "ready", ready_at: new Date().toISOString() } : it
        );
        return { ...o, items: updatedItems, bar_awaiting_delivery: _isAwaitingDelivery(updatedItems) };
      }));
    } else if (isFullSend) {
      recentlyReadyRef.current.set(orderId, Date.now());
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } else {
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const updatedItems = (o.items || []).map((it, idx) =>
          indexSet.has(idx) ? { ...it, status: "ready" } : it
        );
        const stillPending = updatedItems.some(
          (it) => it.status !== "ready" && it.status !== "delivered"
        );
        return stillPending ? { ...o, items: updatedItems } : null;
      }).filter(Boolean));
    }
    setSelectedItems((prev) => { const n = { ...prev }; delete n[orderId]; return n; });

    // Session 30.34 — LAN-first ping for PARTIAL sends too. Until now
    // only the whole-order "Ready" button pinged the mesh / queued an
    // offline event, so on a router-only network a station could not
    // send a single item ahead of the rest — staff only heard about
    // the ticket once everything was marked. Mirror markReady():
    // fire the mesh envelope (with the selected items + partial flag)
    // before the REST call, and fall back to the offline event queue
    // when the cloud is unreachable or the ticket is LAN-only.
    const stationItems = (orderData?.items || []);
    const selectedPayloadItems = indexes
      .map((i) => stationItems[i])
      .filter(Boolean)
      .map((it, n) => ({
        id: it.id || null,
        name: it.name,
        quantity: Number(it.quantity || 1),
        department: it.department || "bar",
        station_index: indexes[n],
      }));
    try {
      fireAndForgetMesh({
        change_id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        event_type: "order_status_changed",
        ts: new Date().toISOString(),
        _mesh_hint: "ready",
        payload: {
          order_id: orderId,
          status: "ready",
          department: "bar",
          order_number: orderData?.order_number || null,
          table_number: orderData?.table_number || null,
          items: selectedPayloadItems,
          partial: !isFullSend,
        },
      }, { targetRole: "*" });
    } catch (_) { /* mesh best-effort */ }
    const queueOffline = async () => {
      await localWrite({
        type: EventType.ORDER_STATUS_CHANGED,
        payload: {
          order_id: orderId,
          order_change_id: orderData?._change_id || null,
          status: "ready",
          department: "bar",
          ready_at: new Date().toISOString(),
          item_indexes: indexes,
          item_ids: selectedPayloadItems.map((it) => it.id).filter(Boolean),
          items: selectedPayloadItems,
          partial: !isFullSend,
        },
        session,
      });
    };
    try {
      if (String(orderId).startsWith("local-")) {
        await queueOffline();
        toast.message("Ready saved — this ticket is still syncing and will update the cloud automatically.");
        return;
      }
      await http.post(`/bar/orders/${orderId}/items-ready`,
        { item_indexes: indexes, item_ids: selectedIds, ...deptScope.scopeBody() }, { headers });
      setTimeout(fetchOrders, 600);
    } catch (error) {
      if (isTransientCloudError(error)) {
        try {
          await queueOffline();
          toast.message("Ready saved on this device — the cloud is unreachable or busy right now; it will sync automatically.");
          return;
        } catch (_) { /* fall through to the revert path */ }
      }
      if (isFullSend && !keepUntilDeliveredRef.current) recentlyReadyRef.current.delete(orderId);
      toast.error(error.response?.data?.detail || "Could not mark items ready — please try again");
      fetchOrders();
    }
  };
  // Track which order IDs we've already shown so the chime only fires
  // when a NEW ticket lands (not on every poll). Mirrors KitchenDashboardPage.
  const knownOrderIdsRef = useRef(null);
  // Session 27.11 — ghost-order suppression (see kitchen).
  const recentlyReadyRef = useRef(new Map());

  useEffect(() => {
    if (!session?.token) navigate("/bar", { replace: true });
  }, [session, navigate]);

  // Session 27.140 — Lock+dirty pattern for Bar (same as Staff/Runner/Kitchen).
  const barFetchInFlightRef = useRef(false);
  const barFetchDirtyRef = useRef(false);

  // Session 28 — Sequence tracking + gap-based reconciliation for Bar.
  const barLastSeqRef = useRef(0);
  const barReconciliationTimerRef = useRef(null);
  const barFetchRef = useRef(null);
  const scheduleBarReconciliation = useCallback(() => {
    if (barReconciliationTimerRef.current) return;
    barReconciliationTimerRef.current = setTimeout(() => {
      barReconciliationTimerRef.current = null;
      if (barFetchRef.current) barFetchRef.current();
    }, 300);
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!headers) return;
    if (!deptScope.readyRef.current) return; // Session 63 — scope not settled yet
    if (barFetchInFlightRef.current) {
      barFetchDirtyRef.current = true;
      return;
    }
    barFetchInFlightRef.current = true;
    barFetchDirtyRef.current = false;
    const fetchStartedAt = Date.now();
    const forceAuthoritative = forceAuthoritativeRef.current === true;
    forceAuthoritativeRef.current = false;
    try {
      const response = await http.get("/bar/orders", { headers, params: deptScope.scopeParams() });
      if (Number(response.data?.long_wait_minutes) > 0) setLongWaitMinutes(Number(response.data.long_wait_minutes));
      if (typeof response.data?.keep_until_delivered === "boolean"
          && response.data.keep_until_delivered !== keepUntilDeliveredRef.current) {
        applyKeepSetting(response.data.keep_until_delivered);
      }
      // Session 55 — business-wide Team mode flag rides along with the list.
      if (typeof response.data.bar_team_mode === "boolean" && response.data.bar_team_mode !== teamModeRef.current) {
        applyTeamMode(response.data.bar_team_mode);
      }
      let next = response.data.orders || [];
      // Session 28b — Capture event_seq for merge, but DO NOT advance
      // barLastSeqRef. Only WS events should advance the seq.
      const serverSeq = (typeof response.data.event_seq === "number") ? response.data.event_seq : 0;
      // Session 27.11 — filter out recently-readied IDs (15s window).
      const recently = recentlyReadyRef.current;
      const cutoff = Date.now() - 15000;
      for (const [id, ts] of Array.from(recently.entries())) {
        if (ts < cutoff) recently.delete(id);
      }
      if (recently.size) {
        next = next.filter((o) => !recently.has(o.id));
      }
      try {
        sessionStorage.setItem(
          "nfcs:bar:last-orders",
          JSON.stringify({ orders: next, at: Date.now() }),
        );
      } catch (_) { /* quota — ignore */ }
      const nextIds = new Set(next.map((o) => o.id));
      if (knownOrderIdsRef.current === null) {
        knownOrderIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const o of next) {
          if (o.bar_awaiting_delivery) continue; // Session 30.24 — cooked tickets aren't new work
          if (!knownOrderIdsRef.current.has(o.id)) { foundNew = true; break; }
        }
        if (foundNew) playBarChime();
        knownOrderIdsRef.current = nextIds;
      }
      // Session 72 — quiet screen → server list replaces; busy → monotonic merge.
      const authoritative = forceAuthoritative || (fetchStartedAt - (lastActivityAtRef.current || 0) > QUIET_BEFORE_AUTHORITATIVE_MS);
      setOrdersRaw((prev) => deptScope.scopeOrders(
        authoritative
          ? replaceStationOrders(prev, next, recentlyReadyRef.current)
          : mergeDeptOrders(prev, next, serverSeq, barLastSeqRef.current, recentlyReadyRef.current)
      ));
    } catch (error) {
      if (error.response?.status === 401) {
        // Session 27.110 — same streak guard as the kitchen board so a
        // single transient 401 doesn't kick the bar off mid-service.
        if (!shouldKickOutOn401("bar")) return;
        const token = session?.token;
        confirmSessionDeadThen("bar", token, () => {
          clearSession("bar", "server_401_confirmed");
          setSessionState(null);
        });
        return;
      }
      // Session 27.24 — same rationale as kitchen fetchOrders:
      // silence transient failures so the 4s poll doesn't spam the
      // bar with red toasts every time Cloudflare has a bad moment.
      const status = error.response?.status;
      const isTransient = !status || status >= 500;
      if (!isTransient) {
        toast.error(error.response?.data?.detail || "Could not load tickets");
      }
    } finally {
      setLoading(false);
      barFetchInFlightRef.current = false;
      if (barFetchDirtyRef.current) {
        barFetchDirtyRef.current = false;
        setTimeout(() => fetchOrders(), 50);
      }
    }
    // Session 69 routing fix relies on this exact re-run set: stable
    // scope pieces only (readyRef / scopeParams), never the whole
    // deptScope object or session.token (headers already covers it);
    // applyTeamMode is a stable setter-wrapper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, applyKeepSetting, deptScope.readyRef, deptScope.scopeParams, deptScope.scopeOrders, setOrders]);
  barFetchRef.current = fetchOrders;

  // Session 63 — re-scope cached tickets + fetch once the department settles.
  useEffect(() => {
    if (!deptScope.readyRef.current) return;
    setOrders((prev) => prev);
    knownOrderIdsRef.current = null;
    fetchOrders();
  }, [deptScope.selected, deptScope.loadingDepts, fetchOrders, setOrders, deptScope.readyRef]);

  // Session 30.24 — Toggle handler (mirror of Kitchen).
  const toggleKeepUntilDelivered = async (value) => {
    if (!headers || savingKeep) return;
    const prevValue = keepUntilDeliveredRef.current;
    applyKeepSetting(value);
    if (value) recentlyReadyRef.current.clear();
    setSavingKeep(true);
    try {
      await http.patch("/bar/settings", { bar_keep_until_delivered: !!value }, { headers });
      toast.success(value
        ? "Ready drinks tickets now stay on screen until a runner or waiter marks them delivered."
        : "Ready drinks tickets now leave the screen as soon as you mark them ready.");
      fetchOrders();
    } catch (error) {
      applyKeepSetting(prevValue);
      toast.error(error.response?.data?.detail || "Could not save the setting — please try again");
    } finally {
      setSavingKeep(false);
    }
  };

  // Session 55 — Team mode toggle (business-wide; every bar screen follows).
  const toggleTeamMode = async (value) => {
    if (!headers || savingTeam) return;
    const prevValue = teamModeRef.current;
    applyTeamMode(value);
    setSavingTeam(true);
    try {
      await http.patch("/bar/settings", { bar_team_mode: !!value }, { headers });
      toast.success(value
        ? "Team mode on — tap “I've got this” on a ticket before making it. Everyone else sees it turn amber and leaves it alone."
        : "Team mode off — tickets go straight to “Mark ready”.");
      fetchOrders();
    } catch (error) {
      applyTeamMode(prevValue);
      toast.error(error.response?.data?.detail || "Could not save the setting — please try again");
    } finally {
      setSavingTeam(false);
    }
  };

  // Session 55 — "I've got this" / "Not me". Optimistic: the ticket turns
  // amber on THIS screen instantly; the server broadcasts to the others.
  const claimOrder = async (orderId, claimed) => {
    if (!headers || claimBusy) return;
    setClaimBusy(orderId);
    const nowIso = new Date().toISOString();
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, bar_claimed: claimed, bar_claimed_at: claimed ? nowIso : null } : o)));
    try {
      await http.post(`/bar/orders/${orderId}/${claimed ? "claim" : "unclaim"}`, {}, { headers });
    } catch (error) {
      if (!isTransientCloudError(error)) {
        setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, bar_claimed: !claimed, bar_claimed_at: claimed ? null : nowIso } : o)));
        toast.error(error.response?.data?.detail || "Could not update the ticket — please try again");
      } else {
        toast.message("Cloud is busy — claim shown on this screen; other screens will catch up when it reconnects.");
      }
    } finally {
      setClaimBusy(null);
    }
  };

  useEffect(() => {
    // Session 27.2 — LOCAL-FIRST BAR PAINT (mirrors kitchen).
    // Hydrate the bar ticket queue from IndexedDB before the network
    // fetch so the dashboard opens instantly on cold-reopen / bad wifi.
    let cancelled = false;
    (async () => {
      try {
        const primed = await hasLocalSnapshot();
        if (!primed || cancelled) return;
        const bundle = await loadDashboardBundle();
        if (!bundle || cancelled) return;
        // Same status filter as the kitchen — the server-side /bar/orders
        // additionally filters items by department, but the local paint
        // is a superset that reconciles as soon as the real fetch lands
        // (~milliseconds later on any working connection).
        const BAR_STATUSES = new Set([
          "pending", "confirmed", "preparing", "ready", "sent_to_bar",
        ]);
        // Session 69 — station-scoped local paint (see the kitchen note):
        // only this bar's live lines, same ready rule as /bar/orders, and
        // never over a list the server has already delivered.
        if (knownOrderIdsRef.current !== null) return;
        const keep = keepUntilDeliveredRef.current;
        const localOrders = (bundle.collections?.orders || [])
          .filter((o) => o && o.status !== "voided" && BAR_STATUSES.has(o.status) && !o.awaiting_staff_confirmation)
          .filter((o) => keep || !o.bar_ready_at)
          .map((o) => {
            const items = (o.items || []).filter((it) => deptScope.lineMatches(it, "bar"));
            if (!items.length) return null;
            return { ...o, items, bar_item_count: items.reduce((n, it) => n + (Number(it.quantity) || 1), 0) };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return ta - tb;
          });
        if (localOrders.length > 0 && !cancelled && knownOrderIdsRef.current === null) {
          setOrders(localOrders);
          setLoading(false);
        }
      } catch (_) { /* best-effort */ }
    })();
    fetchOrders();
    // Session 28 — Safety-net poll only. WS drives the fast path.
    // Session 73 — 6 s, and the safety-net poll ALWAYS lets the server list
    // win (a busy screen never went "quiet" during a rush, so a ticket the
    // server had already dropped — delivered / voided elsewhere — could sit
    // on the board until the next sign-in).
    const RECONCILIATION_INTERVAL = 6000;
    const interval = setInterval(() => { forceAuthoritativeRef.current = true; fetchOrders(); }, RECONCILIATION_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); if (barReconciliationTimerRef.current) { clearTimeout(barReconciliationTimerRef.current); barReconciliationTimerRef.current = null; } };
    // Poll + reconcile cadence is keyed to fetchOrders alone on purpose:
    // deptScope/setOrders are already folded into fetchOrders' identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders]);

  useRealtimeConnection({ baseUrl: BACKEND_URL, token: session?.token });
  const queueOrdersUpdate = useCoalescedUpdater(setOrders);   // Session 70.E — one render per WS burst
  // Session 72 — screen wake / tab focus / network back → pull the truth now.
  useEffect(() => {
    if (!session?.token) return undefined;
    const refresh = () => { if (document.visibilityState !== "hidden" && barFetchRef.current) barFetchRef.current(); };
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", refresh); window.removeEventListener("online", refresh); };
  }, [session?.token]);
  useRealtimeEvent([
    "hello", "state_changed",
    "order_created", "order_updated", "order_status_changed",
    "order_confirmed_by_staff", "business_settings_changed", "bar_claim_changed",
  ], (msg) => {
    // Session 30 — Fresh WS connection: resync seq + reconcile.
    if ((msg?.event_type || msg?.type) === "hello") {
      const serverSeq = Number((msg?.payload || {}).seq);
      barLastSeqRef.current = Number.isFinite(serverSeq) ? serverSeq : 0;
      scheduleBarReconciliation();
      return;
    }
    // Session 72 — any write anywhere → one debounced reconcile.
    if ((msg?.event_type || msg?.type) === "state_changed") {
      if (typeof msg?.seq === "number" && msg.seq > barLastSeqRef.current) barLastSeqRef.current = msg.seq;
      scheduleBarReconciliation();
      return;
    }
    // Session 55 — another bartender claimed / released a ticket.
    if ((msg?.event_type || msg?.type) === "bar_claim_changed") {
      const p = msg?.payload || {};
      if (p.order_id) {
        queueOrdersUpdate((prev) => prev.map((o) => (o.id === p.order_id ? { ...o, bar_claimed: !!p.claimed, bar_claimed_at: p.claimed ? (o.bar_claimed_at || new Date().toISOString()) : null } : o)));
      }
      return;
    }
    // Session 30.24 — another bar screen flipped keep-until-delivered.
    if ((msg?.event_type || msg?.type) === "business_settings_changed") {
      const v = (msg?.payload || {}).bar_keep_until_delivered;
      if (typeof v === "boolean" && v !== keepUntilDeliveredRef.current) {
        applyKeepSetting(v);
        if (v) recentlyReadyRef.current.clear();
        scheduleBarReconciliation();
      }
      const tm = (msg?.payload || {}).bar_team_mode;
      if (typeof tm === "boolean" && tm !== teamModeRef.current) applyTeamMode(tm);
      return;
    }
    try { applyRealtimeEvent(msg); } catch (_) { /* noop */ }

    // Session 28 — Event-driven state architecture for Bar.
    // Direct state mutations from WS events; HTTP fetch only on gaps.
    try {
      const eventType = msg?.event_type || msg?.type;
      const p = msg?.payload || msg || {};
      const orderId = String(p.order_id || p.id || "");
      const seq = typeof msg?.seq === "number" ? msg.seq : 0;

      // Gap detection + dedup
      if (seq > 0) {
        const lastSeq = barLastSeqRef.current;
        if (lastSeq > 0 && seq <= lastSeq) return; // Already processed
        if (lastSeq > 0 && seq > lastSeq + 1) {
          console.warn(`[bar-ws] gap detected: last=${lastSeq} received=${seq} — scheduling reconciliation`);
          scheduleBarReconciliation();
        }
        barLastSeqRef.current = Math.max(barLastSeqRef.current, seq);
      }

      // ── Session 31.13 — VOID: drop voided lines / the whole ticket at once ──
      if (eventType === "order_status_changed" && p.voided === true && orderId) {
        const matchIds = new Set([orderId, p.local_id, p._change_id].filter(Boolean).map(String));
        const matches = (o) => o && (matchIds.has(String(o.id)) || (o._change_id && matchIds.has(String(o._change_id))));
        const voidedIds = new Set((Array.isArray(p.voided_item_ids) ? p.voided_item_ids : []).map(String).filter(Boolean));
        const dropTicket = p.full_void === true || p.status === "voided" || p.status === "cancelled" || p.bar_live === 0;
        const snapQty = new Map((Array.isArray(p.all_items) ? p.all_items : []).filter((it) => it && it.id && !it.voided).map((it) => [String(it.id), Number(it.quantity)]));
        queueOrdersUpdate((prev) => prev.map((o) => {
          if (!matches(o)) return o;
          if (dropTicket) return null;
          let changed = false;
          const items = (o.items || []).filter((it) => it && !it.voided && !voidedIds.has(String(it.id || ""))).map((it) => {
            const q = snapQty.get(String(it.id || ""));
            if (Number.isFinite(q) && q > 0 && q !== Number(it.quantity)) { changed = true; return { ...it, quantity: q }; }
            return it;
          });
          if (items.length === 0) return null;
          if (!changed && items.length === (o.items || []).length) return o;
          return { ...o, items, bar_item_count: items.reduce((n, it) => n + (Number(it.quantity) || 1), 0), bar_awaiting_delivery: _isAwaitingDelivery(items) };
        }).filter(Boolean));
        if (dropTicket) {
          matchIds.forEach((id) => { if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(id); });
          recentlyReadyRef.current.set(orderId, Date.now());
        } else if (voidedIds.size === 0 && snapQty.size === 0) {
          scheduleBarReconciliation();
        }
        return;
      }

      // ── order_created / order_confirmed_by_staff: Add ticket if bar-relevant ──
      if ((eventType === "order_created" || eventType === "order_confirmed_by_staff") && p.order) {
        const fullOrder = p.order;
        // Session 31.20 — gated customer orders must not appear until released.
        if (fullOrder && fullOrder.awaiting_staff_confirmation) return;
        if (fullOrder && fullOrder.id) {
          const barItems = (fullOrder.items || []).filter((it) => {
            // Session 63 — judged against this screen's department scope.
            if (deptScope.lineMatches(it, "bar")) return true;
            const dept = String(it.department || "").trim().toLowerCase();
            return !deptScope.selectedRef.current && dept === "bar / front of house" && !it.voided;
          });
          if (barItems.length > 0 && !recentlyReadyRef.current.has(fullOrder.id)) {
            const barOrder = {
              ...fullOrder,
              items: barItems,
              bar_item_count: barItems.reduce((n, it) => n + (Number(it.quantity) || 1), 0),
              _ws_added_at: Date.now(), // Session 69 — bounds the merge grace (mergeDeptOrders)
            };
            // Session 30.26 / 31.24 — cloud copy replaces the LAN-optimistic
            // ticket that shares its identity (id / _local_id / _change_id).
            // Matching the whole set (not just `_change_id`) is what stops
            // a drinks ticket showing twice on the bar until a refresh.
            const replacesLocal = orders.some((o) => o && o.id !== fullOrder.id && sameOrderIdentity(o, fullOrder));
            queueOrdersUpdate((prev) => {
              if (prev.some((o) => o.id === fullOrder.id)) return prev; // Idempotent
              const idx = prev.findIndex((o) => o && o.id !== fullOrder.id && sameOrderIdentity(o, fullOrder));
              if (idx >= 0) {
                const incomingIsLocal = String(fullOrder.id || "").startsWith("local-");
                const existingIsLocal = String(prev[idx].id || "").startsWith("local-");
                if (incomingIsLocal && !existingIsLocal) return prev;
                const next = prev.slice(); next[idx] = barOrder; return next;
              }
              return [...prev, barOrder];
            });
            if (!replacesLocal && knownOrderIdsRef.current !== null && !knownOrderIdsRef.current.has(fullOrder.id)) {
              playBarChime();
            }
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.add(fullOrder.id);
          }
        } else if (!p.order) {
          scheduleBarReconciliation();
        }
      }

      // ── order_status_changed: handle ready/delivered/completed ──
      if (eventType === "order_status_changed" && orderId && keepUntilDeliveredRef.current) {
        // Session 30.24 — KEEP-UNTIL-DELIVERED mode (mirror of Kitchen).
        const snapshot = Array.isArray(p.all_items) && p.all_items.length
          ? p.all_items
          : (Array.isArray(p.items) ? p.items : []);
        const localOrder = orders.find((o) => o.id === orderId) || null;
        const isBarReady = p.status === "ready" && p.department === "bar";
        // Session 31.6 — a station ack only counts as final when it covered
        // the WHOLE station (`partial` false / `bar_done` true). A partial
        // "Mark delivered" used to yank the ticket while items were still
        // being prepared.
        const isFinal = p.completed === true || p.status === "completed"
          || p.status === "voided" || p.status === "cancelled"
          || p.bar_done === true
          || (p.acknowledged === "bar" && p.partial === false);
        if (isBarReady) {
          if (!localOrder) {
            scheduleBarReconciliation();
          } else if (!p.partial) {
            queueOrdersUpdate((prev) => prev.map((o) => {
              if (o.id !== orderId) return o;
              const items = (o.items || []).map((it) =>
                String(it.status || "").toLowerCase() === "delivered"
                  ? it
                  : { ...it, status: "ready", ready_at: it.ready_at || p.ready_at || new Date().toISOString() }
              );
              return { ...o, items, bar_awaiting_delivery: true };
            }));
          } else {
            const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
            if (merged === localOrder) scheduleBarReconciliation();
            else queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
          }
        } else if (isFinal) {
          queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
          if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
        } else if (localOrder && snapshot.length
          && (p.status === "delivered" || p.acknowledged === "bar" || (Array.isArray(p.delivered_item_ids) && p.delivered_item_ids.length))) {
          const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
          if (merged === localOrder) {
            scheduleBarReconciliation();
          } else if (_isBarDeliveredAll(merged.items)) {
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
          }
        } else if (localOrder && p.status === "delivered") {
          // Session 30.26 — offline delivery without snapshot (see Kitchen).
          const items = (localOrder.items || []).map((it) =>
            String(it.status || "").toLowerCase() === "ready" ? { ...it, status: "delivered" } : it
          );
          if (_isBarDeliveredAll(items)) {
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? { ...o, items, bar_awaiting_delivery: _isAwaitingDelivery(items) } : o)));
          }
          scheduleBarReconciliation();
        }
      } else if (eventType === "order_status_changed" && orderId) {
        if (p.status === "ready" && (p.department === "bar" || p.acknowledged === "bar")) {
          if (!p.partial) {
            recentlyReadyRef.current.set(orderId, Date.now());
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            // Session 29 — Use stable item IDs, filter out empty strings
            const readyItemIds = new Set(
              (p.items || []).map((it) => String(it.id || "")).filter((x) => x !== "")
            );
            if (readyItemIds.size > 0) {
              queueOrdersUpdate((prev) => prev.map((o) => {
                if (o.id !== orderId) return o;
                const updatedItems = (o.items || []).map((it) => {
                  if (readyItemIds.has(String(it.id || ""))) return { ...it, status: "ready" };
                  return it;
                });
                const stillPending = updatedItems.some((it) => it.status !== "ready" && it.status !== "delivered");
                return stillPending ? { ...o, items: updatedItems } : null;
              }).filter(Boolean));
            } else {
              // Legacy fallback: no stable IDs, schedule reconciliation
              scheduleBarReconciliation();
            }
          }
        }
        // Session 31.6 — Delivery / ack events. NEVER drop a ticket that
        // still has bar items to prepare: a runner delivering the
        // items that were ready, or staff acking a partial banner, used to
        // remove the whole ticket. Decide from the explicit station flag
        // or the item snapshot; only hard completion removes blindly.
        const isHardFinal = p.completed === true || p.status === "completed" || p.status === "voided" || p.status === "cancelled";
        const touchesDelivery = p.status === "delivered" || p.acknowledged === "bar"
          || (Array.isArray(p.delivered_item_ids) && p.delivered_item_ids.length > 0);
        if (isHardFinal || p.bar_done === true || (p.acknowledged === "bar" && p.partial === false)) {
          queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
          if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
        } else if (touchesDelivery) {
          const snapshot = Array.isArray(p.all_items) && p.all_items.length ? p.all_items : (Array.isArray(p.items) ? p.items : []);
          const localOrder = orders.find((o) => o.id === orderId) || null;
          if (localOrder && snapshot.length) {
            const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
            const live = (merged.items || []).filter((it) => it && !it.voided);
            const stationFinished = live.length > 0 && live.every((it) => ["ready", "delivered"].includes(String(it.status || "").toLowerCase()));
            if (stationFinished) {
              queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
              if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
            } else if (merged !== localOrder) {
              queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
            }
          } else if (localOrder) {
            scheduleBarReconciliation(); // no snapshot — let the server list decide
          }
        }
      }

      if (eventType === "order_updated") {
        scheduleBarReconciliation();
      }
      // Session 72 — every order event → one debounced reconcile (see Kitchen).
      if (eventType === "order_created" || eventType === "order_status_changed" || eventType === "order_confirmed_by_staff") {
        scheduleBarReconciliation();
      }
    } catch (e) { console.warn("[bar-ws] direct mutation error:", e?.message || e); }
  });

  const markReady = async (orderId) => {
    // Session 30.24 — keep-until-delivered: card stays, no ghost suppression.
    const keep = keepUntilDeliveredRef.current;
    // Session 27.11 — remember this ID so the next fetch doesn't
    // re-surface the ticket while status change propagates.
    if (!keep) recentlyReadyRef.current.set(orderId, Date.now());
    // Session 27.8 — LAN-first "ready" ping (see kitchen markReady).
    try {
      const order = orders.find((o) => o.id === orderId) || null;
      const envelope = {
        change_id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        event_type: "order_status_changed",
        ts: new Date().toISOString(),
        _mesh_hint: "ready",
        payload: {
          order_id: orderId,
          status: "ready",
          department: "bar",
          order_number: order?.order_number || null,
          table_number: order?.table_number || null,
          items: order?.items || [],
        },
      };
      // Session 30.1 — Broadcast to ALL peers ("*") instead of only
      // role="staff". The mesh role of a device is derived from its
      // stored sessions and can lag the dashboard actually on screen
      // (e.g. a tablet that switched roles without logging out), and
      // Runner shares the staff session while Retail has its own —
      // role-targeted delivery silently dropped ready pings for
      // them. Receivers already filter by event_type + payload, so
      // fan-out is safe and cheap (a few hundred bytes per peer).
      fireAndForgetMesh(envelope, { targetRole: "*" });
    } catch (_) { /* mesh best-effort */ }
    // Session 27.127 — Optimistic-first then await API.
    if (keep) {
      const nowIso = new Date().toISOString();
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const items = (o.items || []).map((it) =>
          String(it.status || "").toLowerCase() === "delivered" ? it : { ...it, status: "ready", ready_at: nowIso }
        );
        return { ...o, items, bar_awaiting_delivery: true };
      }));
    } else {
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    }
    setReadyingId(null);
    // Session 30.26 — LAN-optimistic (`local-…`) ticket: skip the REST
    // call (would 404) and queue the transition; backend resolves it.
    const localOrderRef = orders.find((o) => o.id === orderId) || null;
    const isLocalOnly = String(orderId).startsWith("local-");
    try {
      if (isLocalOnly) {
        await localWrite({
          type: EventType.ORDER_STATUS_CHANGED,
          payload: {
            order_id: orderId,
            order_change_id: localOrderRef?._change_id || null,
            status: "ready",
            department: "bar",
            ready_at: new Date().toISOString(),
          },
          session,
        });
        toast.message("Ready saved — this ticket is still syncing and will update the cloud automatically.");
        return;
      }
      await http.post(`/bar/orders/${orderId}/ready`, deptScope.scopeBody(), { headers });
      setTimeout(fetchOrders, 600);
    } catch (error) {
      if (isTransientCloudError(error)) {
        // Session 30.1 — Offline mode: queue the ready transition
        // locally (LAN mesh ping above already notified staff) and
        // let the sync loop deliver it to the cloud later. Deduped
        // by change_id server-side.
        try {
          await localWrite({
            type: EventType.ORDER_STATUS_CHANGED,
            payload: {
              order_id: orderId,
              order_change_id: localOrderRef?._change_id || null,
              status: "ready",
              department: "bar",
              ready_at: new Date().toISOString(),
            },
            session,
          });
          toast.message("Ready saved on this device — the cloud is unreachable or busy right now; it will sync automatically.");
          return;
        } catch (_) { /* fall through to the revert path */ }
      }
      recentlyReadyRef.current.delete(orderId);
      toast.error(error.response?.data?.detail || "Could not mark ready — please try again");
      fetchOrders();
    }
  };

  const logout = async () => {
    beginLogoutSuppression();
    await exitFullscreenIfActive();
    const tokenSnapshot = session?.token;
    clearSession("bar", "user_logout");
    setSessionState(null);
    navigate("/bar", { replace: true });
    try {
      if (tokenSnapshot) {
        await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${tokenSnapshot}` } });
      }
    } catch {
      // no-op — local session already cleared.
    }
  };

  // Auto-logout when the bar tablet leaves the dashboard for 60s+.
  useAutoLogoutOnHidden(logout, { enabled: !!session?.token, graceMs: 60000 });

  if (!session?.token) return null;

  // Session 63 — multi-bar venues: pick the department first (every load).
  if (deptScope.needsPick) {
    return (
      <StationDepartmentPicker
        station="bar"
        departments={deptScope.departments}
        businessName={session.business?.name}
        onSelect={deptScope.choose}
        onLogout={logout}
      />
    );
  }
  const deptLabel = deptScope.selected || (Array.isArray(deptScope.departments) && deptScope.departments.length === 1 ? deptScope.departments[0].name : null);
  const canSwitchDept = Array.isArray(deptScope.departments) && deptScope.departments.length > 1;

  // Session 30.24 — split tickets into in-progress vs awaiting delivery.
  const awaitingDeliveryOrders = keepUntilDelivered
    ? orders.filter((o) => {
        const live = (o.items || []).filter((it) => it && !it.voided && String(it.status || "").toLowerCase() !== "delivered");
        return live.length > 0 && live.every((it) => String(it.status || "").toLowerCase() === "ready");
      })
    : [];
  const awaitingIds = new Set(awaitingDeliveryOrders.map((o) => o.id));
  const inProgressOrders = keepUntilDelivered ? orders.filter((o) => !awaitingIds.has(o.id)) : orders;

  return (
    <main className="min-h-screen bg-background px-4 py-5 sm:px-6 sm:py-7">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
        <header className="flex flex-col gap-3 rounded-[28px] border border-border bg-card p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="bar-header">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-white">
              <Wine className="h-7 w-7" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-indigo-600 text-white hover:bg-indigo-600">Bar station</Badge>
                {deptLabel ? (
                  <Badge variant="outline" className="border-indigo-500/40 text-foreground" data-testid="bar-department-badge">{deptLabel}</Badge>
                ) : null}
              </div>
              <h1 className="mt-2 font-['Space_Grotesk'] text-3xl font-semibold tracking-tight sm:text-4xl">
                {session.business?.name || "Bar Dashboard"}
              </h1>
              <p className="text-sm text-muted-foreground" data-testid="bar-ticket-summary">
                {deptScope.loadingDepts && orders.length === 0
                  ? "Loading departments…"
                  : orders.length === 0
                  ? "All clear — bar is caught up."
                  : keepUntilDelivered
                    ? `${inProgressOrders.length} in progress · ${awaitingDeliveryOrders.length} awaiting delivery`
                    : `${orders.length} active ${orders.length === 1 ? "ticket" : "tickets"}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSwitchDept ? (
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => { setOrdersRaw([]); setLoading(true); deptScope.reset(); }}
                data-testid="bar-switch-department-button"
                title="Choose a different bar department for this screen"
              >
                <ArrowLeftRight className="h-4 w-4" />
                <span className="hidden sm:inline">Switch department</span>
              </Button>
            ) : null}
            {/* Session 55 — Team mode toggle (business-wide). */}
            <label
              className={`flex h-10 cursor-pointer select-none items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${teamMode ? "border-amber-500/60 bg-amber-500/10 text-amber-800 dark:text-amber-300" : "border-border bg-card text-foreground hover:bg-secondary"}`}
              title="Several bartenders on one screen? When on, tap “I've got this” on a ticket before making it — it turns amber on every bar screen so nobody else pours the same drink. “Mark ready” appears only after a ticket is claimed."
              data-testid="bar-team-mode-toggle"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Team mode</span>
              <span className="sm:hidden">Team</span>
              <Switch
                checked={teamMode}
                disabled={savingTeam}
                onCheckedChange={toggleTeamMode}
                aria-label="Team mode — claim tickets before making them"
                data-testid="bar-team-mode-switch"
              />
            </label>
            {/* Session 30.24 — Keep-until-delivered toggle (business-wide, bar only). */}
            <label
              className={`flex h-10 cursor-pointer select-none items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${keepUntilDelivered ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-border bg-card text-foreground hover:bg-secondary"}`}
              title="When on, drinks tickets you mark ready stay on this screen (highlighted) until a runner or waiter marks them delivered."
              data-testid="bar-keep-until-delivered-toggle"
            >
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Keep until delivered</span>
              <span className="sm:hidden">Keep</span>
              <Switch
                checked={keepUntilDelivered}
                disabled={savingKeep}
                onCheckedChange={toggleKeepUntilDelivered}
                aria-label="Keep drinks tickets on screen until delivered"
                data-testid="bar-keep-until-delivered-switch"
              />
            </label>
            <PrepModeButton http={http} headers={headers} station="bar" active={prepMode} onClick={() => setPrepMode((v) => !v)} />
            <MeshStatusPill />
            <ThemeToggleButton />
            <FullscreenToggleButton lockDown />
            <ChimeVolumeControl role="bar" />
            {connectivity.refreshAllowed ? (
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => { fetchOrders(); }}
              data-testid="bar-refresh-button"
              title="Refresh orders"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            ) : null}
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive" onClick={logout} data-testid="bar-logout-button">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>
        {prepMode ? (
          <PrepExecutionPanel http={http} headers={headers} station="bar" session={session} onBack={() => setPrepMode(false)} />
        ) : (
          <>

        {loading && orders.length === 0 ? (
          <Card className="rounded-[28px] border-border bg-card"><CardContent className="p-8"><Skeleton className="h-32 w-full" /></CardContent></Card>
        ) : null}

        {!loading && orders.length === 0 ? (
          <Card className="rounded-[28px] border-2 border-dashed border-border bg-card">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-500/10">
                <CheckCircle2 className="h-10 w-10 text-indigo-500" />
              </div>
              <p className="font-['Space_Grotesk'] text-2xl font-semibold">All caught up!</p>
              <p className="max-w-md text-sm text-muted-foreground">
                New drink tickets will appear here automatically. The page refreshes every 5 seconds.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {(() => {
          const renderTicket = (order) => {
            // Session 27.103 — Hide items already marked ready so the
            // ticket shrinks to just the pending items.
            // Session 30.24 — keep-until-delivered: ready items stay visible.
            const visibleItems = (order.items || []).filter(
              (it) => (it && it.status !== "delivered" && (keepUntilDelivered || it.status !== "ready"))
            );
            if (visibleItems.length === 0) return null;
            const isAwaiting = keepUntilDelivered && awaitingIds.has(order.id);
            const isLongWait = !isAwaiting && ticketWaitMinutes(order, stationNow) >= longWaitMinutes;
            // Session 55 — Team mode: a claimed ticket is AMBER everywhere so
            // the other bartenders leave it alone.
            const isClaimed = teamMode && !isAwaiting && !!(order.bar_claimed || order.bar_claimed_at);
            return (
            <Card
              key={order.id}
              className={`overflow-hidden rounded-[28px] border-2 shadow-md transition hover:shadow-lg ${isAwaiting ? "border-emerald-500 bg-card ring-2 ring-emerald-400/30" : isClaimed ? "border-amber-500 bg-amber-50 ring-2 ring-amber-400/40 dark:bg-amber-950/30" : isLongWait ? "border-rose-500 bg-card ring-4 ring-rose-400/40 animate-[nfcsLongWaitPulse_1.6s_ease-in-out_infinite]" : "border-border bg-card hover:border-indigo-500"}`}
              data-testid={`bar-ticket-${order.id}`}
              data-long-wait={isLongWait ? "true" : undefined}
              data-awaiting-delivery={isAwaiting ? "true" : undefined}
              data-claimed={isClaimed ? "true" : undefined}
            >
              {isLongWait && !isClaimed ? <style>{`@keyframes nfcsLongWaitPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.45); } 50% { box-shadow: 0 0 0 10px rgba(244,63,94,0); } }`}</style> : null}
              <div className={`flex items-center justify-between px-5 py-4 text-white ${isAwaiting ? "bg-emerald-600" : isClaimed ? "bg-amber-500" : isLongWait ? "bg-rose-600" : "bg-indigo-600"}`}>
                <div>
                  <p className="text-xs uppercase tracking-wider opacity-80">Table</p>
                  <p className="font-['Space_Grotesk'] text-4xl font-bold leading-none" data-testid={`bar-ticket-table-${order.id}`}>
                    {order.table_number || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider opacity-80">Order</p>
                  <p className="font-mono text-base font-semibold">{order.order_number}</p>
                  {isLongWait ? (
                    <div className="mt-1"><LongWaitTicketBadge minutes={ticketWaitMinutes(order, stationNow)} testId={`bar-ticket-long-wait-${order.id}`} /></div>
                  ) : (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs opacity-80">
                      <Timer className="h-3 w-3" />
                      <KitchenTimeAgo from={order.created_at} />
                    </p>
                  )}
                </div>
              </div>
              {isAwaiting ? (
                <div
                  className="flex items-center gap-2 border-b border-emerald-500/30 bg-emerald-500/15 px-5 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                  data-testid={`bar-ticket-awaiting-delivery-${order.id}`}
                >
                  <Truck className="h-4 w-4" />
                  Ready — awaiting delivery
                </div>
              ) : isClaimed ? (
                <div
                  className="flex items-center justify-between gap-2 border-b border-amber-500/40 bg-amber-500/20 px-5 py-2 text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200"
                  data-testid={`bar-ticket-claimed-${order.id}`}
                >
                  <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> Claimed — being made</span>
                  <button
                    type="button"
                    className="rounded-md px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal underline-offset-2 hover:underline disabled:opacity-50"
                    onClick={() => claimOrder(order.id, false)}
                    disabled={claimBusy === order.id}
                    title="Release this ticket so someone else can take it"
                    data-testid={`bar-unclaim-button-${order.id}`}
                  >
                    Not me
                  </button>
                </div>
              ) : null}
              <CardContent className="space-y-3 p-5">
                <div className="space-y-2">
                  {(order.items || []).map((item, idx) => {
                    // Session 27.102 — mirror KitchenDashboardPage. Use
                    // the array index as the selection key so we send
                    // reliable item_indexes[] to the backend.
                    const itemId = String(idx);
                    const isSelected = (selectedItems[order.id] || new Set()).has(itemId);
                    const alreadyReady = item.status === "ready" || item.status === "delivered";
                    // Session 27.103 — Hide items already marked ready.
                    // Session 30.24 — unless keep-until-delivered is on.
                    const showReadyRow = keepUntilDelivered && item.status === "ready";
                    if (item.voided) return null; // Session 31.2 — never show voided lines
                    if (alreadyReady && !showReadyRow) return null;
                    return (
                    <div key={`${order.id}-${idx}`} className={`flex items-start gap-3 rounded-2xl border ${alreadyReady ? "border-emerald-500/50 bg-emerald-500/10" : "border-border bg-secondary/40"} px-4 py-3`} data-item-status={item.status || "pending"}>
                      {/* Session 27.101 — Per-item selection checkbox. */}
                      {alreadyReady ? (
                        <CheckCircle2 className="mt-2 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Ready" />
                      ) : (
                      <input
                        type="checkbox"
                        className="mt-2 h-5 w-5 accent-indigo-600"
                        checked={isSelected}
                        onChange={() => _toggleItem(order.id, itemId)}
                        data-testid={`bar-item-check-${order.id}-${idx}`}
                      />
                      )}
                      <div className="flex h-10 min-w-10 items-center justify-center rounded-xl bg-indigo-600 px-2 font-['Space_Grotesk'] text-lg font-bold text-white">
                        {item.quantity}×
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold leading-tight">{item.name}{alreadyReady ? <span className="ml-2 inline-flex items-center rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid={`bar-item-ready-pill-${order.id}-${idx}`}>Ready</span> : null}</p>
                        {Array.isArray(item.prep_selections) && item.prep_selections.length ? (
                          <ul className="mt-1 space-y-0.5 text-xs text-foreground">
                            {item.prep_selections.map((p, pIdx) => (
                              <li key={`bp-${idx}-${pIdx}`} className="flex items-center gap-1">
                                <span aria-hidden className="text-indigo-500">*</span>
                                <span className="font-semibold">{p.label}:</span>
                                <span className="uppercase tracking-wide">{p.choice}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {item.note ? (
                          <p className="mt-1 rounded-lg bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" data-testid={`bar-line-note-${idx}`}>
                            <span className="mr-1">📝</span>{item.note}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    );
                  })}
                </div>
                {/* Session 27.103 — Single color-changing action button
                    (mark whole ticket). Blue when items selected = partial
                    send. Prevents accidentally shipping the entire ticket
                    when the operator only meant to send a subset. */}
                {isAwaiting ? (
                  <div
                    className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-500/50 bg-emerald-500/10 text-sm font-semibold text-emerald-700 dark:text-emerald-300"
                    data-testid={`bar-awaiting-delivery-${order.id}`}
                    role="status"
                  >
                    <Truck className="h-5 w-5" />
                    Waiting for a runner / waiter to deliver
                  </div>
                ) : teamMode && !isClaimed ? (
                  // Session 55 — step 1 of 2: claim it. "Mark ready" only
                  // appears once the ticket is claimed (amber).
                  <Button
                    className="h-14 w-full rounded-2xl bg-amber-500 text-base font-semibold text-white shadow-md hover:bg-amber-600 disabled:opacity-60"
                    onClick={() => claimOrder(order.id, true)}
                    disabled={claimBusy === order.id}
                    data-testid={`bar-claim-button-${order.id}`}
                  >
                    <Users className="h-5 w-5" />
                    {claimBusy === order.id ? "Claiming…" : "I've got this"}
                  </Button>
                ) : (() => {
                  const selectedCount = (selectedItems[order.id] && selectedItems[order.id].size) || 0;
                  const isPartial = selectedCount > 0;
                  const label = readyingId === order.id
                    ? "Sending…"
                    : isPartial
                      ? `Send ${selectedCount} Selected`
                      : "Drinks Ready";
                  const colorClass = isPartial
                    ? "bg-blue-600 hover:bg-blue-700"
                    : isClaimed ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700";
                  return (
                    <Button
                      className={`h-14 w-full rounded-2xl ${colorClass} text-base font-semibold text-white shadow-md disabled:opacity-60`}
                      onClick={() => (isPartial ? _markSelectedReady(order.id) : markReady(order.id))}
                      disabled={readyingId === order.id}
                      data-testid={`bar-ready-button-${order.id}`}
                    >
                      <Check className="h-5 w-5" />
                      {label}
                    </Button>
                  );
                })()}
              </CardContent>
            </Card>
            );
          };
          return (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="bar-in-progress-grid">
                {inProgressOrders.map(renderTicket)}
              </div>
              {keepUntilDelivered && awaitingDeliveryOrders.length > 0 ? (
                <section className="flex flex-col gap-4" data-testid="bar-awaiting-delivery-section">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white">
                      <Truck className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-['Space_Grotesk'] text-xl font-semibold leading-tight">Ready — awaiting delivery</h2>
                      <p className="text-xs text-muted-foreground">
                        {awaitingDeliveryOrders.length} {awaitingDeliveryOrders.length === 1 ? "ticket" : "tickets"} poured and waiting for a runner or waiter. They clear automatically once marked delivered.
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="bar-awaiting-delivery-grid">
                    {awaitingDeliveryOrders.map(renderTicket)}
                  </div>
                </section>
              ) : null}
            </>
          );
        })()}
          </>
        )}
      </div>
    </main>
  );
}
